import { useQuery } from "react-query"
import { Box, Button, Chip, Collapse, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { TaskListData, TaskRecord, TaskStatus, TaskType, rMapTaskStatus, rMapTaskType } from "../types/Task";
import { useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import React from "react";
import { TaskCollectionType, TaskRecordCollectionData, TaskRecordCollectionDetailedData, rMapTaskCollectionType } from "../types/TaskCollection";
import { getDetailedTaskRecordCollection, listTaskCollections, updateTaskRecordCollection } from "../api/taskCollection";
import { updateTaskRecord } from "../api/task";

export const JobListTaskRecordCollection = (props: {
    currentLocationID: number,
    currentLocationName: string,
    shouldShowAction?: boolean
}) => {
    const {currentLocationID, currentLocationName} = props
    const shouldShowAction = !!props.shouldShowAction ? props.shouldShowAction : false

    const [allTasks, setAllTasks] = useState<TaskRecordCollectionData[]>([]);
    const openIndexContainer = useRef<number>(-1)
    const [refreshPage, setRefreshPage] = useState<boolean>(false);
    const [currentListOfNavigations, setCurrentListOfNavigations] = useState<TaskRecordCollectionDetailedData | null>(null)
    const [queryId, setQueryId] = useState<number>(4); // set as some default
    const [latestTaskRecordCollectorId, setLatestTaskRecordCollectorId] = useState<number>(0)
    const [playNewTaskTone, setPlayNewTaskTone] = useState<boolean>(false)
    const audio = useRef(new Audio("./assets/sound/iphone-ding.mp3"))

    // Note: this is done to simplify some logic and improve performance. No need to query for these locations every time
    const localLocationMapping : {[name: number] : string}= {
        50: "test_L1b",
        51: "lift_point_L1b",
        52: "inside_lift_L1",
        53: "L1Charge",
        54: "inside_lift_point_L14",
        55: "lift_point_L14",
        56: "door_1_L14",
        57: "door_2_L14",
        58: "door_3_L14",
        59: "door_4_L14",
        60: "nurse_station_L14",
        61: "Setup Point",
        62: "Setup Point", // note: in db shows as abc
        63: "j1_L14",
        64: "j1_L1",
    }

    const rMapSeverity: {[name: number] : "info" | "warning" | "success" | "error"}   = { // based on task status index
        0: "info",
        1: "success",
        2: "error",
        3: "info",
        4: "info",
        5: "warning",
        6: "info",
        7: "error",
        8: "error",
        9: "success", 
        10: "info",
        11: "success",
        12: "success",
        13: "warning"
    }

    useQuery("queryTaskRecords", async () => {
        const tasks = (await listTaskCollections()).data as TaskRecordCollectionData[];
        // console.log(tasks)
        if(tasks[0].id > latestTaskRecordCollectorId) {
            setLatestTaskRecordCollectorId(tasks[0].id)
            if (latestTaskRecordCollectorId !== 0) {
                setPlayNewTaskTone(true)
            }
        }
        setAllTasks(tasks)
    }, {
        refetchInterval: 5000
    })

    useQuery("detailedTaskRecord", async () => {
        const result = (await getDetailedTaskRecordCollection(queryId)).data as TaskRecordCollectionDetailedData
        // const result = (await getDetailedTaskRecordCollection(4)).data as TaskRecordCollectionDetailedData
        setCurrentListOfNavigations(result)
    }, {
        refetchInterval: 1000,
        // enabled: refreshPage
    })

    useEffect(() => {
        if (refreshPage) {
            setCurrentListOfNavigations(null)
            setRefreshPage(false);
        }
    }, [refreshPage])

    useEffect(() => {
        if(playNewTaskTone) {
            audio.current.play();
            console.log("Playing audio")
            setPlayNewTaskTone(false);
        }
    }, [playNewTaskTone])

    const handleRequeueNavigation = async (tr: TaskRecord) => {
        // to requeue we just need to change the status
        tr.status = TaskStatus.QUEUED
        const result = await updateTaskRecord(tr)
        setRefreshPage(true)
    }

    const handleCompleteNavigation = async (tr: TaskRecord) => {
        // to complte we just need to change the status
        tr.status = TaskStatus.COMPLETED
        const result = await updateTaskRecord(tr)
        setRefreshPage(true)
    }

    const handleResumeTask = async (trc: TaskRecordCollectionData) => {
        
        if (trc.taskCollectionType === TaskCollectionType.STANDARD_JOB) {
            if(trc.progressIndex <= 1) {
                // resume to source
                trc.progressIndex = 0
                trc.status = TaskStatus.IN_PROGRESS
            } else {
                // resume to target
                trc.progressIndex = 2
                trc.status = TaskStatus.IN_PROGRESS
            }
        } else if (trc.taskCollectionType === TaskCollectionType.RESEND_JOB) {
            // resend tasks never need to go back to source, as it is assumed it is at source already
            trc.progressIndex = 0
            trc.status = TaskStatus.QUEUED
        } else if (trc.taskCollectionType === TaskCollectionType.CHARGING_JOB) {
            // charging job just sends to charge when job is queued
            trc.status = TaskStatus.QUEUED
        }
        const result = await updateTaskRecordCollection(trc)
        setRefreshPage(true)
    }

    const handleCancelTask = async (trc: TaskRecordCollectionData) => {
        trc.status = TaskStatus.REQUEST_CANCELLED
        const result = await updateTaskRecordCollection(trc)
        setRefreshPage(true)
    }

    const CreateRow = (taskRecord: TaskRecordCollectionData, currentIndex: number) => {
        let displayCreatedDate = "-"
        if (taskRecord.createdDatetime !== null && taskRecord.createdDatetime !== undefined) {
            const recordCompletedDate = new Date(taskRecord.createdDatetime);
            displayCreatedDate = recordCompletedDate.toLocaleDateString('en-us', { weekday:"short", year:"numeric", month:"short", day:"numeric"})
        }

        let displayCreatedTime = "-"
        if (taskRecord.createdDatetime !== null && taskRecord.createdDatetime !== undefined) {
            const recordCompletedTime = new Date(taskRecord.createdDatetime);
            displayCreatedTime = recordCompletedTime.toLocaleTimeString();
        }

        let displayCompletedDate = "-"
        if (taskRecord.endDatetime !== null && taskRecord.endDatetime !== undefined) {
            const recordCompletedDate = new Date(taskRecord.endDatetime);
            displayCompletedDate = recordCompletedDate.toLocaleDateString('en-us', { weekday:"short", year:"numeric", month:"short", day:"numeric"})
        }
        let displayCompletedTime = ""
        if (taskRecord.endDatetime !== null && taskRecord.endDatetime !== undefined) {
            const recordCompletedTime = new Date(taskRecord.endDatetime);
            displayCompletedTime = recordCompletedTime.toLocaleTimeString();
        }
        return <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                {taskRecord.taskCollectionType === TaskCollectionType.STANDARD_JOB 
                || taskRecord.taskCollectionType === TaskCollectionType.RESEND_JOB
                || taskRecord.taskCollectionType === TaskCollectionType.CHARGING_JOB ?
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => {
                            if (openIndexContainer.current === currentIndex){
                                setQueryId(4);
                                openIndexContainer.current = -1
                            } else {
                                setQueryId(taskRecord.id);
                                openIndexContainer.current = currentIndex;
                            }
                            setRefreshPage(true)
                        }}
                    >
                        {currentIndex ===  openIndexContainer.current ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
                : <></>}
                </TableCell>
                <TableCell>{taskRecord.id}</TableCell>
                {/* <TableCell>{taskRecord.taskType === TaskType.LOCAL_JOB ? 
                            <b>{rMapTaskType[taskRecord.taskType]}</b> :
                            <p>{rMapTaskType[taskRecord.taskType]}</p>}</TableCell> *This is hard coded, we have index for now */}
                <TableCell><p>{rMapTaskCollectionType[taskRecord.taskCollectionType]}</p></TableCell>
                <TableCell>{(localLocationMapping[taskRecord.sourceLocation] === localLocationMapping[taskRecord.targetLocation]) && taskRecord.taskCollectionType === TaskCollectionType.CHARGING_JOB ? localLocationMapping[60] : localLocationMapping[taskRecord.sourceLocation]}</TableCell>
                <TableCell>{localLocationMapping[taskRecord.targetLocation]}</TableCell>
                <TableCell>
                    <Chip label={rMapTaskStatus[taskRecord.status]} color={rMapSeverity[taskRecord.status]}/>
                </TableCell>
                <TableCell>
                    <p>{displayCreatedTime}</p>
                    <p>{displayCreatedDate}</p>
                    </TableCell>
                <TableCell>
                    <p>{displayCompletedTime}</p>
                    <p>{displayCompletedDate}</p>
                </TableCell>
                <TableCell>
                    { taskRecord.status === TaskStatus.COMPLETED || taskRecord.status === TaskStatus.FAILED
                        || taskRecord.status === TaskStatus.FAILED_LOCAL || taskRecord.status === TaskStatus.REQUEST_CANCELLED
                        ? <Button disabled>Completed</Button>
                        : <Button onClick={() => handleResumeTask(taskRecord)}>Resume</Button>}
                    {taskRecord.status === TaskStatus.COMPLETED || taskRecord.status === TaskStatus.FAILED
                        || taskRecord.status === TaskStatus.FAILED_LOCAL || taskRecord.status === TaskStatus.REQUEST_CANCELLED
                        ? <Button disabled>CANCELLED</Button>
                        : <Button onClick={() => handleCancelTask(taskRecord)}>Cancel Job</Button>}
                </TableCell>
            </TableRow>
            {taskRecord.taskCollectionType === TaskCollectionType.STANDARD_JOB 
                || taskRecord.taskCollectionType === TaskCollectionType.RESEND_JOB
                || taskRecord.taskCollectionType === TaskCollectionType.CHARGING_JOB ? <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10} sx={{
                backgroundColor: "#F4F4F4"
            }}>
                <Collapse in={currentIndex ===  openIndexContainer.current} timeout="auto" unmountOnExit>
                    <Typography variant="h6" gutterBottom component="div">
                        {"Path taken for " + taskRecord.id}
                    </Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Navigation ID</TableCell>
                                <TableCell>To Location</TableCell>
                                <TableCell>Start Time</TableCell>
                                <TableCell>Completed Time</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {currentIndex === openIndexContainer.current && currentListOfNavigations !== null 
                        ? currentListOfNavigations.taskRecords.map(tr => tr.path.map((locationRecord) => {
                            const completedDatetime = tr.completedDatetime
                            var completedDatetimeString = ""
                            var displayCompletedTime = ""
                            
                            const startTime = tr.createdDatetime
                            var startDatetimeString = ""
                            var displayStartTime = ""
                            if(!!completedDatetime) {
                                const timeOfJobCompletion = new Date(completedDatetime)
                                // console.log(timeOfJobCompletion)
                                completedDatetimeString = timeOfJobCompletion.toLocaleDateString('en-us', { weekday:"short", year:"numeric", month:"short", day:"numeric"})
                                displayCompletedTime = timeOfJobCompletion.toLocaleTimeString()
                            }
                            if(!!startTime) {
                                const timeOfJobCompletion = new Date(startTime)
                                // console.log(timeOfJobCompletion)
                                startDatetimeString = timeOfJobCompletion.toLocaleDateString('en-us', { weekday:"short", year:"numeric", month:"short", day:"numeric"})
                                displayStartTime = timeOfJobCompletion.toLocaleTimeString()
                            }
                            return <TableRow key={completedDatetimeString}>
                                <TableCell>
                                    {tr.id}
                                </TableCell>
                                <TableCell>
                                    {locationRecord.location.name}
                                </TableCell>
                                <TableCell>
                                    {/* <p>{completedDatetimeString}</p> */}
                                    {/* <p></p> */}
                                    {displayStartTime}
                                </TableCell>
                                <TableCell>
                                    {/* <p>{completedDatetimeString}</p> */}
                                    {/* <p></p> */}
                                    {displayCompletedTime}
                                </TableCell>
                                <TableCell>
                                    <Chip label={rMapTaskStatus[locationRecord.status]} color={rMapSeverity[locationRecord.status]}/>
                                </TableCell>
                                <TableCell>
                                    {tr.status === TaskStatus.COMPLETED || tr.status === TaskStatus.FAILED
                                        || tr.status === TaskStatus.FAILED_LOCAL || tr.status === TaskStatus.REQUEST_CANCELLED
                                        ? <Button disabled>Completed</Button>
                                        : <Button onClick={() => handleCompleteNavigation(tr)}>Complete</Button>}
                                    {tr.status === TaskStatus.QUEUED 
                                        ? <Button disabled>Navigation queued</Button>
                                        : <Button onClick={() => handleRequeueNavigation(tr)}>Requeue navigation</Button>}
                                </TableCell>
                            </TableRow>
                        })).reverse()
                        : <></>}
                        </TableBody>
                    </Table>
                </Collapse>
                </TableCell>
            </TableRow> : <></>}
        </React.Fragment>
    }
    return <>
    <TableContainer sx={{
        height: "80vh"
    }}>
    <Table  stickyHeader sx={{
        width: "100%",
        marginLeft: "0px",
        height: "max-content",
        overflow: "hidden"
    }}>
        <TableHead>
            <TableRow>
                <TableCell/>
                <TableCell>Job Number</TableCell>
                <TableCell>Job Type</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Time</TableCell>
                <TableCell>Completed Time</TableCell>
                <TableCell>Actions</TableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {allTasks && allTasks.map((taskRecord, i) => CreateRow(taskRecord, i))}
        </TableBody>
    </Table>
    </TableContainer>
    </>
}

